/**
 * DisclosureTable Component
 *
 * Main table with:
 * - Expandable rows for disclosure history
 * - Checkbox selection
 * - Loan details display
 * - eConsent and ITOP dates
 */

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, AlertCircle, AlertTriangle, AlertOctagon, CheckCircle2, Clock, RefreshCw, ExternalLink, Send, FileCheck, Lock, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { DisclosureLoan, DisclosureStatus, SortColumn, SortDirection } from '@/types/disclosure-dashboard';
import { cn } from '@/lib/utils';
import { EnhancedDisclosureHistory } from './EnhancedDisclosureHistory';
import { SendDisclosureModal } from './SendDisclosureModal';
import { preValidateDisclosure, type PreValidationResult } from '@/services/disclosurePreValidationService';

interface DisclosureTableProps {
  loans: DisclosureLoan[];
  loading: boolean;
  error: string | null;
  selectedIds: Set<string>;
  expandedLoanId: string | null;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onToggleSelect: (loanId: string) => void;
  onSelectAll: (checked: boolean) => void;
  onToggleExpand: (loanId: string) => void;
  onSort: (column: SortColumn) => void;
  onRefresh: () => void;
}

// Sortable column header
interface SortableHeaderProps {
  column: SortColumn;
  label: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  className?: string;
}

function SortableHeader({
  column,
  label,
  sortColumn,
  sortDirection,
  onSort,
  className
}: SortableHeaderProps) {
  const isActive = sortColumn === column;

  return (
    <th
      className={cn(
        'p-3 cursor-pointer hover:bg-muted/50 select-none transition-colors',
        className
      )}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </th>
  );
}

// Missing data badge - shows when required disclosure fields are missing
function MissingDataBadge({ loan }: { loan: DisclosureLoan }) {
  const validation = preValidateDisclosure(loan);

  if (validation.isComplete) {
return null;
}

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="bg-amber-100 text-amber-800 text-xs whitespace-nowrap">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Missing Data
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs">
          <div className="font-medium mb-1">Missing Fields:</div>
          {validation.missingFields.map(f => (
            <div key={f.fieldId}>
              {f.fieldId}: {f.fieldName}
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Abbreviate name for compact display: "Anthony Amini" -> "A. Amini"
function getAbbreviatedName(fullName: string | null): string {
  if (!fullName) {
return 'In Use';
}
  const parts = fullName.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}. ${parts[parts.length - 1]}`;
  }
  return fullName;
}

// File lock indicator - shows when someone has the loan open in Encompass Smart Client
function FileLockIndicator({ loan }: { loan: DisclosureLoan }) {
  if (!loan.is_file_locked) {
return null;
}

  const formatLockTime = (time: string | null) => {
    if (!time) {
return null;
}
    try {
      return new Date(time).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  const lockTime = formatLockTime(loan.file_lock_time);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-xs">
          <Lock className="h-3 w-3 flex-shrink-0" />
          <span className="font-medium truncate max-w-[80px]">
            {getAbbreviatedName(loan.file_locked_by_name)}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs">
          <div className="font-medium text-red-600 mb-1">
            Loan File Locked
          </div>
          <div className="text-muted-foreground">
            {loan.file_locked_by_name
              ? `${loan.file_locked_by_name} has this loan open`
              : 'Someone has this loan open in Encompass'}
          </div>
          {lockTime && (
            <div className="text-muted-foreground mt-0.5">
              Since: {lockTime}
            </div>
          )}
          <div className="text-muted-foreground mt-1 italic">
            API changes will fail while locked
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Status badge component - shows disclosure workflow status with icon + color + label
function StatusBadge({ status }: { status: DisclosureStatus }) {
  switch (status) {
    case 'not_sent':
      return (
        <Badge className="bg-red-100 text-red-800 text-xs whitespace-nowrap">
          <Send className="h-3 w-3 mr-1" />
          Send LE
        </Badge>
      );
    case 'le_overdue':
      return (
        <Badge className="bg-red-600 text-white text-xs whitespace-nowrap">
          <AlertOctagon className="h-3 w-3 mr-1" />
          LE Overdue
        </Badge>
      );
    case 'itop_missing':
      return (
        <Badge className="bg-orange-100 text-orange-800 text-xs whitespace-nowrap">
          <AlertTriangle className="h-3 w-3 mr-1" />
          ITOP Missing
        </Badge>
      );
    case 'itop_received':
      return (
        <Badge className="bg-blue-100 text-blue-800 text-xs whitespace-nowrap">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          ITOP ✓
        </Badge>
      );
    case 'cd_sent':
      return (
        <Badge className="bg-purple-100 text-purple-800 text-xs whitespace-nowrap">
          <Clock className="h-3 w-3 mr-1" />
          CD Pending
        </Badge>
      );
    case 'complete':
      return (
        <Badge className="bg-green-100 text-green-800 text-xs whitespace-nowrap">
          <FileCheck className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs">
          {status}
        </Badge>
      );
  }
}

// Format currency
function formatCurrency(amount: number | null): string {
  if (amount == null) {
return '-';
}
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Format date (use UTC to prevent timezone shift)
function formatDate(date: string | null): string {
  if (!date) {
return '-';
}
  try {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      timeZone: 'UTC'
    });
  } catch {
    return '-';
  }
}

// Expanded Row Content
function DisclosureHistory({ loan }: { loan: DisclosureLoan }) {
  const disclosures = [];

  // Build disclosure history from available data
  if (loan.le_initial_sent_date) {
    disclosures.push({
      type: 'Initial',
      form: 'LE',
      hasItop: !!loan.le_itop_sent_date,
      dateSent: loan.le_initial_sent_date,
      dateReceived: loan.le_initial_received_date
    });
  }

  if (loan.le_redisclosure_sent_date) {
    disclosures.push({
      type: 'Revised',
      form: 'LE',
      hasItop: true, // Revised LE implies ITOP was received
      dateSent: loan.le_redisclosure_sent_date,
      dateReceived: loan.le_redisclosure_received_date
    });
  }

  if (loan.cd_sent_date) {
    disclosures.push({
      type: 'Initial',
      form: 'CD',
      hasItop: null, // N/A for CD
      dateSent: loan.cd_sent_date,
      dateReceived: loan.cd_received_date
    });
  }

  if (disclosures.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic py-2">
        No disclosures sent yet
      </div>
    );
  }

  return (
    <div className="py-2">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">Disclosure History</h4>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pb-1 pr-4">Type</th>
            <th className="pb-1 pr-4">Form</th>
            <th className="pb-1 pr-4">ITOP?</th>
            <th className="pb-1 pr-4">Date Sent</th>
            <th className="pb-1 pr-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {disclosures.map((d, idx) => (
            <tr key={idx} className={cn("border-b border-border/50", idx === disclosures.length - 1 && "border-b-0")}>
              <td className="py-1.5 pr-4">{d.type}</td>
              <td className="py-1.5 pr-4">{d.form}</td>
              <td className="py-1.5 pr-4">
                {d.hasItop === null ? (
                  <Badge variant="outline" className="text-xs">N/A</Badge>
                ) : d.hasItop ? (
                  <Badge className="bg-green-100 text-green-800 text-xs">YES</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800 text-xs">NO</Badge>
                )}
              </td>
              <td className="py-1.5 pr-4">{formatDate(d.dateSent)}</td>
              <td className="py-1.5 pr-4">
                {d.dateReceived ? (
                  <span className="text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {formatDate(d.dateReceived)}
                  </span>
                ) : (
                  <span className="text-amber-600 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Pending
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Loading skeleton
function TableSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

// Empty state
function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <h3 className="text-lg font-medium text-foreground mb-1">No loans found</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Try adjusting your filters or refresh the data
      </p>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="h-4 w-4 mr-1" />
        Refresh
      </Button>
    </div>
  );
}

// Error state
function ErrorState({ error, onRefresh }: { error: string; onRefresh: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-red-500/50 mb-4" />
      <h3 className="text-lg font-medium text-foreground mb-1">Error loading data</h3>
      <p className="text-sm text-muted-foreground mb-4">{error}</p>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="h-4 w-4 mr-1" />
        Try Again
      </Button>
    </div>
  );
}

export function DisclosureTable({
  loans,
  loading,
  error,
  selectedIds,
  expandedLoanId,
  sortColumn,
  sortDirection,
  onToggleSelect,
  onSelectAll,
  onToggleExpand,
  onSort,
  onRefresh
}: DisclosureTableProps) {
  const { toast } = useToast();

  // State for Send Disclosure Modal
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendModalLoan, setSendModalLoan] = useState<DisclosureLoan | null>(null);
  const [preValidation, setPreValidation] = useState<PreValidationResult | null>(null);

  // State for copy feedback
  const [copiedLoanId, setCopiedLoanId] = useState<string | null>(null);

  // Copy loan number to clipboard
  const handleCopyLoanNumber = async (loanNumber: string, loanId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row expansion
    try {
      await navigator.clipboard.writeText(loanNumber);
      setCopiedLoanId(loanId);
      toast({
        description: `Copied ${loanNumber} to clipboard`
      });
      // Reset after 2 seconds
      setTimeout(() => setCopiedLoanId(null), 2000);
    } catch {
      toast({
        variant: 'destructive',
        description: 'Failed to copy to clipboard'
      });
    }
  };

  // Handle opening the send modal
  const handleOpenSendModal = (loan: DisclosureLoan) => {
    // Run pre-validation to check for missing required fields
    const validation = preValidateDisclosure(loan);
    setPreValidation(validation);
    setSendModalLoan(loan);
    setSendModalOpen(true);
  };

  // Handle successful send
  const handleSendSuccess = () => {
    onRefresh();
  };

  if (loading) {
return <TableSkeleton />;
}
  if (error) {
return <ErrorState error={error} onRefresh={onRefresh} />;
}
  if (loans.length === 0) {
return <EmptyState onRefresh={onRefresh} />;
}

  const allSelected = loans.length > 0 && selectedIds.size === loans.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < loans.length;

  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-muted/50 backdrop-blur z-10">
          <tr className="text-left text-muted-foreground text-xs font-medium">
            <th className="p-3 w-10">
              <Checkbox
                checked={someSelected ? 'indeterminate' : allSelected}
                onCheckedChange={(checked) => onSelectAll(checked === true)}
              />
            </th>
            <SortableHeader column="trans_details_loan_number" label="Loan #" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="borrower_name" label="Borrower" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th className="p-3">Status</th>
            <th className="p-3 text-center w-24">Actions</th>
            <SortableHeader column="milestone" label="Milestone" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="trans_details_loan_amt" label="Amount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} className="text-right" />
            <SortableHeader column="property_address" label="Property" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="trans_details_loan_purpose" label="Purpose" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="loan_officer_name" label="LO" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="econsent_date" label="eConsent" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="application_date" label="App Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="le_itop_received_date" label="ITOP" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="le_initial_sent_date" label="LE Sent" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader column="rate_lock_buy_side_lock_expires" label="Lock" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {loans.map(loan => {
            const isExpanded = expandedLoanId === loan.id;
            const isSelected = selectedIds.has(loan.id);

            return (
              <React.Fragment key={loan.id}>
                {/* Main Row */}
                <tr
                  className={cn(
                    'border-b border-border/60 hover:bg-muted/40 cursor-pointer transition-all',
                    isExpanded && 'bg-muted/30 border-b-0'
                  )}
                  onClick={() => onToggleExpand(loan.id)}
                >
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(loan.id)}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      {loan.trans_details_loan_number ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={(e) => handleCopyLoanNumber(loan.trans_details_loan_number!, loan.id, e)}
                              className="font-mono text-xs hover:text-primary hover:underline cursor-pointer flex items-center gap-1 group"
                            >
                              {loan.trans_details_loan_number}
                              {copiedLoanId === loan.id ? (
                                <Check className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Click to copy loan number</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="font-mono text-xs">-</span>
                      )}
                      <FileLockIndicator loan={loan} />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{loan.borrower_name}</div>
                    {loan.coborrower_name && (
                      <div className="text-xs text-muted-foreground">{loan.coborrower_name}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      {loan.disclosure_status === 'le_overdue' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span><StatusBadge status={loan.disclosure_status} /></span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {loan.days_since_app
                              ? `${loan.days_since_app} business days since application (TRID: 3 day limit)`
                              : 'LE not sent within 3 business days of application'}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <StatusBadge status={loan.disclosure_status} />
                      )}
                      {/* Show missing data badge for loans that need disclosures */}
                      {(loan.disclosure_status === 'not_sent' || loan.disclosure_status === 'le_overdue') && (
                        <MissingDataBadge loan={loan} />
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      {/* Send Disclosures Button - show if no LE sent yet */}
                      {!loan.le_initial_sent_date && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-8 w-8",
                                loan.is_file_locked
                                  ? "text-muted-foreground cursor-not-allowed opacity-50"
                                  : "text-green-600 hover:text-green-700 hover:bg-green-50"
                              )}
                              onClick={() => !loan.is_file_locked && handleOpenSendModal(loan)}
                              disabled={!!loan.is_file_locked}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {loan.is_file_locked
                              ? `Cannot send - ${loan.file_locked_by_name || 'Someone'} has this loan open`
                              : 'Send Initial Disclosures'}
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Consumer Link - show if available */}
                      {loan.consumer_link ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={loan.consumer_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-colors"
                            >
                              <ExternalLink className="h-4 w-4 text-blue-600" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>Open Consumer Connect Portal</TooltipContent>
                        </Tooltip>
                      ) : loan.le_initial_sent_date ? (
                        <span className="text-muted-foreground text-xs">-</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-xs">{loan.milestone || '-'}</span>
                  </td>
                  <td className="p-3 text-right font-medium">
                    {formatCurrency(loan.trans_details_loan_amt)}
                  </td>
                  <td className="p-3">
                    <div className="text-xs max-w-[200px] truncate" title={loan.property_address || ''}>
                      {loan.property_address || '-'}
                    </div>
                    {loan.subject_property_city && (
                      <div className="text-xs text-muted-foreground">
                        {loan.subject_property_city}, {loan.subject_property_state}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs">{loan.trans_details_loan_purpose || '-'}</td>
                  <td className="p-3 text-xs">{loan.loan_officer_name}</td>
                  <td className="p-3 text-xs">{formatDate(loan.econsent_date)}</td>
                  <td className="p-3 text-xs">{formatDate(loan.application_date)}</td>
                  <td className="p-3 text-xs">
                    {loan.le_itop_received_date ? (
                      <span className="text-green-700 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {formatDate(loan.le_itop_received_date)}
                      </span>
                    ) : loan.le_initial_sent_date ? (
                      <Badge className="bg-red-100 text-red-800 text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        Missing
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {formatDate(loan.le_initial_sent_date)}
                  </td>
                  <td className="p-3 text-xs">
                    {loan.rate_lock_status === 'Locked' ? (
                      <div className="flex flex-col">
                        <Badge className="bg-green-100 text-green-800 text-xs w-fit">Locked</Badge>
                        {loan.rate_lock_buy_side_lock_expires && (
                          <span className="text-muted-foreground mt-0.5">
                            Exp: {formatDate(loan.rate_lock_buy_side_lock_expires)}
                          </span>
                        )}
                      </div>
                    ) : loan.rate_lock_status === 'Expired' ? (
                      <Badge className="bg-red-100 text-red-800 text-xs">Expired</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>

                {/* Expanded Row */}
                {isExpanded && (
                  <tr className="bg-muted/20 border-b border-border/60">
                    <td colSpan={15} className="p-0">
                      <div className="ml-8 mr-4 my-3 p-4 bg-background border border-border/70 rounded-lg shadow-sm">
                        <EnhancedDisclosureHistory encompassLoanId={loan.encompass_loan_id} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Send Disclosure Modal */}
      {sendModalLoan && (
        <SendDisclosureModal
          open={sendModalOpen}
          onOpenChange={setSendModalOpen}
          loan={sendModalLoan}
          onSuccess={handleSendSuccess}
          preValidation={preValidation}
          preloadedAudit={
            sendModalLoan.has_valid_audit_cache && sendModalLoan.disclosure_audit_id && sendModalLoan.disclosure_audit_result
              ? {
                  auditId: sendModalLoan.disclosure_audit_id,
                  auditResult: sendModalLoan.disclosure_audit_result as any,
                  packageType: sendModalLoan.disclosure_audit_package_type || 'AtApp',
                  planCodeId: sendModalLoan.disclosure_audit_plan_code_id || null,
                  isValid: sendModalLoan.has_valid_audit_cache
                }
              : null
          }
        />
      )}
    </div>
  );
}
